import React, { useState } from "react";
import { Box, List, ListItemButton, ListItemIcon, ListItemText, useTheme, Typography, BottomNavigation, BottomNavigationAction, Drawer, useMediaQuery, alpha, } from "@mui/material";
import { NavLink, useLocation } from "react-router-dom";
import { LogoutCurve } from "iconsax-react";
import { Icon } from "@iconify/react";
import { SAvatar } from "@ui/components/avatar/SAvatar";
import { menuConfig, mobileNavConfig } from "./menuConfig";
import { useAppSelector } from "@application/store/useStore";
import userService from "@utility/user-service";
import { PermissionChecker, Permission } from "@utility/permission-checker";
import LanguageSwitcher from "@ui/components/language-switcher/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import { SButton } from "@ui/components/buttons";
import { useFreeRoamingNodesSummary } from "@ui/hooks/useFreeRoamingNodesSummary";
export const MobileNavigation = ({ onLogout, }) => {
    const theme = useTheme();
    const location = useLocation();
    const { t } = useTranslation("common");
    const [moreDrawerOpen, setMoreDrawerOpen] = useState(false);
    const activeColor = "#00478D";
    const inactiveColor = theme.palette.text.secondary;
    // Get user data from Redux or localStorage as fallback
    const { user } = useAppSelector((state) => state.auth);
    const userName = (user === null || user === void 0 ? void 0 : user.name) || userService.getUserDisplayName();
    const userEmail = (user === null || user === void 0 ? void 0 : user.email) || userService.getUserEmail();
    const isActive = (path) => {
        return (location.pathname === path || location.pathname.startsWith(path + "/"));
    };
    const getItemColor = (path) => {
        return isActive(path) ? activeColor : inactiveColor;
    };
    // Map sidebar paths to required permissions
    const freeRoamingSummary = useFreeRoamingNodesSummary();
    const pathPermissions = {
        "/dashboard": Permission.DASHBOARD_VIEW,
        "/nodes": Permission.NODES_VIEW,
        "/free-roaming-nodes": Permission.NODES_VIEW,
        "/alarms": Permission.ALARMS_VIEW,
        "/insights": Permission.INSIGHTS_VIEW,
        "/reports": Permission.REPORTS_VIEW,
        "/users": Permission.USERS_VIEW,
        "/roles": Permission.ROLES_VIEW,
        "/settings": Permission.SETTINGS_VIEW,
    };
    // Helper function to check if dashboard should be visible
    const canViewDashboard = () => {
        return (PermissionChecker.hasPermission(Permission.DASHBOARD_VIEW) ||
            PermissionChecker.hasPermission(Permission.ACCESSPOINTS_VIEW));
    };
    // Free Roaming Nodes: Super Admin only.
    const canViewFreeRoamingNodes = () => {
        if (!PermissionChecker.hasPermission(Permission.NODES_VIEW))
            return false;
        return freeRoamingSummary.isSuperAdmin;
    };
    // Get all items sorted by priority for mobile, filtered by permissions
    const allItems = menuConfig
        .flatMap((group) => group.items)
        .filter((item) => {
        // Special handling for dashboard - show if user has Dashboard.View OR AccessPoints.View
        if (item.path === "/dashboard") {
            return canViewDashboard();
        }
        if (item.path === "/free-roaming-nodes") {
            return canViewFreeRoamingNodes();
        }
        const requiredPermission = pathPermissions[item.path];
        return (!requiredPermission ||
            PermissionChecker.hasPermission(requiredPermission));
    })
        .sort((a, b) => a.priority - b.priority);
    // For iPad, show more items in bottom nav
    const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
    // Determine visible and hidden items based on device type
    const visibleCount = isTablet
        ? mobileNavConfig.tablet.visibleCount
        : mobileNavConfig.phone.visibleCount;
    const visibleItems = allItems.slice(0, visibleCount);
    const hiddenItems = allItems.slice(visibleCount);
    const handleMoreClick = () => {
        setMoreDrawerOpen(true);
    };
    const handleMoreDrawerClose = () => {
        setMoreDrawerOpen(false);
    };
    // Find the current active item from visible items
    const getCurrentValue = () => {
        const activeItem = visibleItems.find((item) => isActive(item.path));
        if (activeItem)
            return activeItem.path;
        // Check if any hidden item is active (for "More" button selection)
        const activeHiddenItem = hiddenItems.find((item) => isActive(item.path));
        if (activeHiddenItem)
            return "more";
        return false;
    };
    // Check if any hidden item is currently active
    const isMoreActive = hiddenItems.some((item) => isActive(item.path));
    return (React.createElement(React.Fragment, null,
        React.createElement(Box, { sx: { display: { xs: "block", lg: "none" } } },
            React.createElement(BottomNavigation, { showLabels: true, value: getCurrentValue(), sx: {
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.paper,
                    zIndex: theme.zIndex.appBar,
                    height: isTablet ? 70 : 70,
                    py: 1,
                } },
                visibleItems.map((item) => (React.createElement(BottomNavigationAction, { key: item.path, label: t(item.label), value: item.path, icon: item.icon(getItemColor(item.path)), component: NavLink, to: item.path, sx: {
                        color: inactiveColor,
                        "&.Mui-selected": {
                            color: activeColor,
                            backgroundColor: "#1D95E214",
                            borderRadius: 1,
                            mx: 0.5,
                        },
                        "& .MuiBottomNavigationAction-label": {
                            fontSize: isTablet ? 13 : 12,
                            fontWeight: isActive(item.path) ? 600 : 400,
                        },
                        "& .MuiSvgIcon-root, & svg": {
                            fontSize: isTablet ? 22 : 20,
                        },
                        minWidth: isTablet ? 80 : "auto",
                        maxWidth: isTablet ? 120 : "auto",
                    } }))),
                hiddenItems.length > 0 && (React.createElement(BottomNavigationAction, { label: t("sidebar.more"), value: "more", icon: React.createElement(Icon, { icon: "fluent:more-circle-32-filled", width: isTablet ? "22" : "20", color: isMoreActive ? activeColor : inactiveColor }), onClick: handleMoreClick, sx: {
                        color: isMoreActive ? activeColor : inactiveColor,
                        "&.Mui-selected": {
                            color: activeColor,
                            backgroundColor: "#1D95E214",
                            borderRadius: 1,
                            mx: 0.5,
                        },
                        "& .MuiBottomNavigationAction-label": {
                            fontSize: isTablet ? 13 : 12,
                            fontWeight: isMoreActive ? 600 : 400,
                        },
                        minWidth: isTablet ? 80 : "auto",
                        maxWidth: isTablet ? 120 : "auto",
                    } })))),
        React.createElement(Drawer, { anchor: "bottom", open: moreDrawerOpen, onClose: handleMoreDrawerClose, sx: {
                display: { xs: "block", lg: "none" },
                "& .MuiDrawer-paper": {
                    borderTopLeftRadius: 20,
                    borderTopRightRadius: 20,
                    maxHeight: "85vh",
                    pb: "env(safe-area-inset-bottom)", // Handle notch on iOS
                },
            } },
            React.createElement(Box, { sx: {
                    width: 40,
                    height: 5,
                    backgroundColor: theme.palette.divider,
                    borderRadius: 2.5,
                    mx: "auto",
                    mt: 1.5,
                    mb: 1,
                } }),
            React.createElement(Box, { sx: { display: "flex", justifyContent: "center", pt: 2 } },
                React.createElement(Box, { component: "img", src: "/assets/images/logo.svg", alt: "SiteX logo" })),
            React.createElement(Box, { sx: { px: 2, pb: 2 } },
                React.createElement(List, { sx: { py: 0 } }, hiddenItems.map((item) => (React.createElement(ListItemButton, { key: item.path, component: NavLink, to: item.path, onClick: handleMoreDrawerClose, selected: isActive(item.path), sx: {
                        borderRadius: 3,
                        mb: 0.5,
                        py: 1.75,
                        px: 1.5,
                        "&.Mui-selected": {
                            backgroundColor: alpha(activeColor, 0.08),
                            "&:hover": {
                                backgroundColor: alpha(activeColor, 0.12),
                            },
                        },
                        "&:hover": {
                            backgroundColor: alpha(theme.palette.action.hover, 0.04),
                        },
                    } },
                    React.createElement(ListItemIcon, { sx: {
                            minWidth: 36,
                            color: getItemColor(item.path),
                        } }, item.icon(getItemColor(item.path))),
                    React.createElement(ListItemText, { primary: t(item.label), primaryTypographyProps: {
                            color: getItemColor(item.path),
                            fontWeight: isActive(item.path) ? 600 : 500,
                            fontSize: 15,
                        } }))))),
                React.createElement(Box, { sx: {
                        // mx: 2,
                        mt: 2,
                        p: 1,
                        borderRadius: 2,
                        backgroundColor: theme.palette.background.paper,
                    } },
                    React.createElement(Box, { sx: {
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                        } },
                        React.createElement(SAvatar, { size: 52, name: userName }),
                        React.createElement(Box, { sx: { flex: 1, minWidth: 0 } },
                            React.createElement(Typography, { variant: "subtitle1", sx: {
                                    fontWeight: 600,
                                    fontSize: 16,
                                    mb: 0.25,
                                    lineHeight: 1.3,
                                } }, userName),
                            React.createElement(Typography, { variant: "body2", color: "text.secondary", sx: {
                                    fontSize: 13,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                } }, userEmail))),
                    React.createElement(Box, { sx: {
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        } },
                        React.createElement(LanguageSwitcher, null),
                        React.createElement(SButton, { variant: "soft", color: "error", fullWidth: true, sx: { borderRadius: 5 }, size: "small", startIcon: React.createElement(LogoutCurve, null), onClick: () => {
                                handleMoreDrawerClose();
                                onLogout();
                            } }, t("sidebar.logout"))))))));
};
